import { observer } from "mobx-react-lite"
import { getTripDatesInfo } from "@cruisen/react-shared"
import { useEffect, useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import ChevronLeft from "~icons/eva/chevron-left-outline"
import styles from "../components/organisms/CruiseDetails/styles.module.scss"
import { TripCard } from "../components/organisms/CruiseDetails/cards/CruiseDetailsTripCard"
import { ShipCard } from "../components/organisms/CruiseDetails/cards/CruiseDetailsShipCard"
import { CruiseDetailsHero } from "../components/organisms/CruiseDetails/CruiseDetailsHero"
import classNames from "classnames"
import { CabinsCard } from "../components/organisms/CruiseDetails/cards/CruiseDetailsCabinCard"
import { DecksCard } from "../components/organisms/CruiseDetails/cards/CruiseDetailsDecksCard"
import { Analytics } from "../utils/analytics"
import { useParams, useNavigate } from "react-router-dom"
import { getUnauthenticatedCruiseDetails } from "../services/firebase-functions/cruiseDetails"
import { LoadingScreen } from "../components/templates/LoadingScreen"
import { DownloadMobileAppBanner } from "../components/atoms/DownloadMobileAppBanner"
import { makeAutoObservable } from "mobx"
import { ModalWrapper } from "../components/templates/ModalWrapper"
import { DownloadMobileAppModal } from "../components/modals/DownloadMobileAppModal"
import LikeButton from "../components/atoms/LikeButton"
import { PriceDetails } from "../components/atoms/PriceDetails"
import { DiscountPriceDetails } from "../components/atoms/DiscountPriceDetails"
import { Helmet } from "react-helmet-async"

class CruiseDetailsStore {
  isDownloadAppModalVisible: boolean

  constructor() {
    this.isDownloadAppModalVisible = false

    makeAutoObservable(this)
  }

  setIsDownloadAppModalVisible(isVisible: boolean) {
    this.isDownloadAppModalVisible = isVisible
  }
}

export const CruiseDetails = observer(function CruiseDetails() {
  const tripSearchId = "main"
  const { tripId, cruiseId } = useParams()
  const navigate = useNavigate()
  const store = useMemo(() => new CruiseDetailsStore(), [])

  const cruiseDetailsQueryKey = ["cruiseDetails", tripId, cruiseId]

  const {
    data: cruiseDetailsData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: cruiseDetailsQueryKey,
    queryFn: () => getUnauthenticatedCruiseDetails(tripId, tripSearchId, cruiseId),
    enabled: !!cruiseId,
  })

  const cruiseDetails = cruiseDetailsData?.cruiseDetails
  const tripInviteLink = cruiseDetailsData?.tripInviteLink

  useEffect(() => {
    if (cruiseDetails) {
      Analytics.logViewItem({ name: cruiseDetails.tripSearchResult.name, id: cruiseId })
    }
  }, [cruiseId, cruiseDetails])

  const areParamsValid = !!cruiseId && !!tripId

  if (!areParamsValid) {
    return (
      <div className={styles.cardContainer}>
        <div className={styles.errorContainer}>
          <p className={styles.errorText}>Incorrect URL</p>
          <p className={styles.errorSubText}>Please check the URL and try again</p>
        </div>
      </div>
    )
  }

  if (cruiseDetails === null || isError) {
    return (
      <div className={styles.cardContainer}>
        <div className={styles.errorContainer}>
          <p className={styles.errorText}>Cruise details not found</p>
          <p className={styles.errorSubText}>Please try again later</p>
        </div>
      </div>
    )
  }

  const isLoaded = cruiseDetailsData !== undefined && !isLoading

  if (!isLoaded) return <LoadingScreen />

  const { ship, itineraryItems } = cruiseDetails
  const cruiseSearchResult = cruiseDetails.tripSearchResult

  const heroImages = ship.images.sort(({ url }) => (url === ship.imageUrl ? -1 : 1))

  return (
    <div className={styles.cardContainer}>
      <Helmet>
        <title>{cruiseDetails.tripSearchResult.name}</title>
        <link
          rel="canonical"
          href={`${window.location.origin}/trips/${tripId}/cruises/${cruiseId}`}
        />
        <meta property="og:title" content={cruiseDetails.tripSearchResult.name} />
        <meta property="og:image" content={cruiseDetails.ship.imageUrl} />
        <meta
          property="og:url"
          content={`${window.location.origin}/trips/${tripId}/cruises/${cruiseId}`}
        />
        <meta property="og:site_name" content="Cruisen" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content={cruiseDetails.tripSearchResult.name} />
        <meta name="twitter:image" content={cruiseDetails.ship.imageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <div className={styles.container}>
        <div
          className={classNames(styles.cruiseDetails, styles.cruiseDetailsWithoutBorderRadius)}
          data-testid="cruise-details"
        >
          <DownloadMobileAppBanner
            onInstall={() => {
              store.setIsDownloadAppModalVisible(true)
            }}
          />
          <div className={styles.carouselContainer}>
            <CruiseDetailsHero images={heroImages} roundedTop={false} />
            <div className={styles.cruiseLineLogoContainer}>
              <img
                src={cruiseDetails.tripSearchResult.lineLogo}
                className={styles.cruiseLineLogo}
                alt={cruiseDetails.tripSearchResult.lineName}
              />
            </div>
          </div>
          <div className={styles.title}>
            <button className={styles.backPageButton} onClick={() => navigate(`/trips/${tripId}`)}>
              <ChevronLeft className={styles.backPageButtonIcon} />
              <p className={styles.backPageButtonText}>All cruises</p>
            </button>
            <p className={styles.cruiseSailingName}>{cruiseDetails.tripSearchResult.name}</p>
            <p className={styles.cruiseLineName}>{cruiseDetails.tripSearchResult.lineName}</p>
          </div>

          <div className={styles.cardsList}>
            <TripCard
              itineraryItems={itineraryItems}
              isLoadingData={false}
              tripDatesInfo={getTripDatesInfo(
                cruiseSearchResult.startDate,
                cruiseSearchResult.endDate,
              )}
            />
            <ShipCard ship={ship} />
            <DecksCard decks={cruiseDetails.decks} />
            <CabinsCard
              cabinGradeTypes={cruiseSearchResult.cabinGradeTypes}
              cabinGrades={cruiseDetails.cabinGrades}
              cabinsScanStatus={cruiseSearchResult.cabinsScanStatus}
              cabinGradesScanStatus={cruiseSearchResult.cabinGradesScanStatus}
              cabinGradeDiscounts={cruiseSearchResult.cabinGradeDiscounts}
            />
          </div>

          <div className={styles.cruiseDetailsFooter}>
            <div>
              {cruiseSearchResult.lowestPricePP.discount ? (
                <DiscountPriceDetails pricePP={cruiseSearchResult.lowestPricePP} />
              ) : (
                <PriceDetails pricePP={cruiseSearchResult.lowestPricePP} />
              )}
            </div>
            <div className={styles.mainRow}>
              <LikeButton
                userId={null}
                tripId={tripId}
                tripSearchId={cruiseId}
                docId={cruiseId}
                cruiseLikes={[]}
                onClick={() => {
                  store.setIsDownloadAppModalVisible(true)
                }}
                testId={"cruise-details-footer-like-button"}
              />
              <div className={styles.buttonRow}>
                <button
                  className={classNames(styles.selectCruiseButton)}
                  onClick={() => {
                    store.setIsDownloadAppModalVisible(true)
                  }}
                >
                  Select
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {store.isDownloadAppModalVisible && (
        <ModalWrapper>
          <DownloadMobileAppModal
            tripInviteLink={tripInviteLink}
            closeModal={() => store.setIsDownloadAppModalVisible(false)}
          />
        </ModalWrapper>
      )}
    </div>
  )
})
